import { Link } from "react-router-dom";
import type { CaseItem } from "@/mocks/cases";
import { formatHeat } from "@/utils/format";

interface CaseSidebarProps {
  item: CaseItem;
  related: CaseItem[];
}

export default function CaseSidebar({ item, related }: CaseSidebarProps) {
  const typeCount: Record<string, number> = {};
  item.evidenceList.forEach((ev) => {
    typeCount[ev.type] = (typeCount[ev.type] ?? 0) + 1;
  });

  return (
    <aside className="flex flex-col gap-5 lg:sticky lg:top-24">
      <div className="rounded-lg border border-background-200 bg-background-50 p-5">
        <h2 className="font-heading text-base font-bold text-foreground-950">档案信息</h2>
        <dl className="mt-4 flex flex-col gap-3 text-sm">
          <div className="flex items-center justify-between gap-4">
            <dt className="text-foreground-500">档案编号</dt>
            <dd className="text-foreground-800 font-medium">{item.id.toUpperCase()}</dd>
          </div>
          <div className="flex items-center justify-between gap-4">
            <dt className="text-foreground-500">所属分类</dt>
            <dd>
              <Link
                to={`/archive?cat=${item.category}`}
                className="text-primary-700 hover:text-primary-500 transition-colors cursor-pointer"
              >
                {item.categoryName}
              </Link>
            </dd>
          </div>
          <div className="flex items-center justify-between gap-4">
            <dt className="text-foreground-500">建立时间</dt>
            <dd className="text-foreground-800">{item.publishedAt}</dd>
          </div>
          <div className="flex items-center justify-between gap-4">
            <dt className="text-foreground-500">补充状态</dt>
            <dd className="text-foreground-800">{item.status}</dd>
          </div>
          <div className="flex items-center justify-between gap-4">
            <dt className="text-foreground-500">热度</dt>
            <dd className="text-foreground-800">{formatHeat(item.heat)}</dd>
          </div>
        </dl>
      </div>

      <div className="rounded-lg border border-background-200 bg-background-50 p-5">
        <h2 className="font-heading text-base font-bold text-foreground-950">证据构成</h2>
        <ul className="mt-4 flex flex-wrap gap-2">
          {Object.entries(typeCount).map(([type, count]) => (
            <li key={type} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-secondary-100 text-secondary-900 text-xs">
              <i className="ri-links-line" />
              {type} × {count}
            </li>
          ))}
        </ul>
        <div className="mt-4 flex flex-wrap gap-1.5">
          {item.tags.map((tag) => (
            <span key={tag} className="px-2 py-0.5 rounded-md border border-background-200 text-xs text-foreground-600">
              #{tag}
            </span>
          ))}
        </div>
      </div>

      <div className="rounded-lg border border-accent-200 bg-accent-50 p-5">
        <h2 className="font-heading text-base font-bold text-foreground-950">补充或更正这条档案</h2>
        <p className="mt-2 text-sm leading-relaxed text-foreground-700">
          如果你掌握新材料，或认为我们的整理有疏漏，欢迎在投递时注明档案编号 {item.id.toUpperCase()}。
        </p>
        <Link
          to="/archive#tips"
          className="mt-4 inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-md bg-accent-500 text-primary-950 text-sm font-medium whitespace-nowrap hover:bg-accent-600 transition-colors cursor-pointer"
        >
          <i className="ri-megaphone-line" />
          投递材料
        </Link>
      </div>

      {related.length > 0 && (
        <div className="rounded-lg border border-background-200 bg-background-50 p-5">
          <h2 className="font-heading text-base font-bold text-foreground-950">相关档案</h2>
          <ul className="mt-4 flex flex-col divide-y divide-background-200">
            {related.map((rel) => (
              <li key={rel.id} className="py-3 first:pt-0 last:pb-0">
                <Link
                  to={`/archive/${rel.id}`}
                  className="group block cursor-pointer"
                >
                  <p className="text-sm font-medium leading-snug text-foreground-800 group-hover:text-primary-700 transition-colors line-clamp-2">
                    {rel.title}
                  </p>
                  <p className="mt-1.5 flex items-center gap-3 text-xs text-foreground-500">
                    <span>{rel.categoryName}</span>
                    <span className="flex items-center gap-1">
                      <i className="ri-fire-line" />
                      {formatHeat(rel.heat)}
                    </span>
                  </p>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}
    </aside>
  );
}